import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import AutomationModal, { type AutomationRule } from "./components/AutomationModal";
import { getTrigger, getAction } from "./automationConfig";
import LoadingState from "@/components/base/LoadingState";
import ModuleGate from "@/components/base/ModuleGate";

type FilterTab = "all" | "active" | "inactive";

export default function AdminAutomations() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [tab, setTab] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [selected, setSelected] = useState<AutomationRule | null>(null);

  const fetchRules = useCallback(async (tid: string) => {
    setLoading(true);
    const { data, error } = await supabase
      .from("automation_rules")
      .select("*")
      .eq("tenant_id", tid)
      .order("created_at", { ascending: false });
    if (!error && data) setRules(data as AutomationRule[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchRules(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchRules]);

  const filtered = rules.filter((r) => {
    const matchTab =
      tab === "all" ? true :
      tab === "active" ? r.is_active :
      !r.is_active;
    const matchSearch =
      r.label.toLowerCase().includes(search.toLowerCase()) ||
      r.trigger.toLowerCase().includes(search.toLowerCase()) ||
      r.action.toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  const activeCount   = rules.filter((r) => r.is_active).length;
  const inactiveCount = rules.filter((r) => !r.is_active).length;

  /* ── Toggle active ─────────────────────────── */
  const handleToggle = async (rule: AutomationRule) => {
    if (!tenantId) return;
    setTogglingId(rule.id);
    const { data, error } = await supabase
      .from("automation_rules")
      .update({ is_active: !rule.is_active })
      .eq("id", rule.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else if (data) {
      setRules((prev) => prev.map((r) => (r.id === rule.id ? (data as AutomationRule) : r)));
    }
    setTogglingId(null);
  };

  /* ── Delete ────────────────────────────────── */
  const handleDelete = async (rule: AutomationRule) => {
    const result = await Swal.fire({
      title: "Delete rule?",
      text: `"${rule.label}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed || !tenantId) return;
    const { error } = await supabase
      .from("automation_rules")
      .delete()
      .eq("id", rule.id)
      .eq("tenant_id", tenantId);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else {
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
    }
  };

  /* ── Save (create / update) ────────────────── */
  const handleSave = async (data: Omit<AutomationRule, "id" | "tenant_id" | "created_at">) => {
    if (!tenantId) return;

    if (isNew) {
      const { data: created, error } = await supabase
        .from("automation_rules")
        .insert({ ...data, tenant_id: tenantId })
        .select()
        .maybeSingle();
      if (error) {
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
      if (created) setRules((prev) => [created as AutomationRule, ...prev]);
      Swal.fire({ title: "Rule created!", icon: "success", timer: 1500, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
    } else if (selected) {
      const { data: updated, error } = await supabase
        .from("automation_rules")
        .update(data)
        .eq("id", selected.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();
      if (error) {
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
      if (updated) setRules((prev) => prev.map((r) => (r.id === selected.id ? (updated as AutomationRule) : r)));
      Swal.fire({ title: "Saved!", icon: "success", timer: 1200, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
    }
    setModalOpen(false);
  };

  const handleNew = () => { setSelected(null); setIsNew(true); setModalOpen(true); };
  const handleEdit = (rule: AutomationRule) => { setSelected(rule); setIsNew(false); setModalOpen(true); };

  return (
    <AdminLayout>
      <AdminHeader
        title="Automations"
        subtitle="Configurable trigger → action rules for your studio"
        actions={
          <button
            onClick={handleNew}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </span>
            New Rule
          </button>
        }
      />

      <ModuleGate moduleId="email_automations">
      <div className="px-4 md:px-8 py-4 md:py-7">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-7">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 text-[#C8A96E] shrink-0">
              <i className="ri-flashlight-line text-lg"></i>
            </div>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider">Total Rules</p>
              <p className="text-[#0f172a] text-2xl font-bold">{rules.length}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-400 shrink-0">
              <i className="ri-play-circle-line text-lg"></i>
            </div>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider">Active</p>
              <p className="text-emerald-400 text-2xl font-bold">{activeCount}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 text-slate-400 shrink-0">
              <i className="ri-pause-circle-line text-lg"></i>
            </div>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider">Inactive</p>
              <p className="text-slate-400 text-2xl font-bold">{inactiveCount}</p>
            </div>
          </div>
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-3 mb-6">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search rules…"
              className="w-full bg-white rounded-xl border border-slate-200 border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Tab filter */}
          <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1">
            {(["all", "active", "inactive"] as FilterTab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                  tab === t ? "bg-[#C8A96E] text-black" : "text-slate-400 hover:text-[#0f172a]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <span className="text-slate-400 text-sm ml-auto">{filtered.length} rules</span>
        </div>

        {/* Rules list */}
        {loading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <EmptyState
            hasRules={rules.length > 0}
            onNew={handleNew}
          />
        ) : (
          <div className="space-y-3">
            {filtered.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                toggling={togglingId === rule.id}
                onToggle={handleToggle}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <AutomationModal
          rule={selected}
          isNew={isNew}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
      </ModuleGate>
    </AdminLayout>
  );
}

/* ─── Rule Card ──────────────────────────────────────────────── */
interface RuleCardProps {
  rule: AutomationRule;
  toggling: boolean;
  onToggle: (r: AutomationRule) => void;
  onEdit: (r: AutomationRule) => void;
  onDelete: (r: AutomationRule) => void;
}

function RuleCard({ rule, toggling, onToggle, onEdit, onDelete }: RuleCardProps) {
  const trigger = getTrigger(rule.trigger);
  const action  = getAction(rule.action);

  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 rounded-xl border transition-all ${
        rule.is_active ? "border-slate-200" : "border-white/[0.03]"
      }`}
    >
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Active indicator */}
        <div className={`w-1.5 h-10 rounded-full shrink-0 transition-colors ${
          rule.is_active ? "bg-emerald-400" : "bg-white/10"
        }`} />

        {/* Trigger → Action flow */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shrink-0 ${trigger.color}`}>
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={`${trigger.icon} text-xs`}></i>
            </span>
            <span className="whitespace-nowrap">{trigger.label}</span>
          </div>

          <div className="w-8 h-px bg-white/10 relative shrink-0">
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              <i className="ri-arrow-right-s-line text-slate-300 text-xs"></i>
            </div>
          </div>

          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shrink-0 ${action.color}`}>
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={`${action.icon} text-xs`}></i>
            </span>
            <span className="whitespace-nowrap">{action.label}</span>
          </div>

          {/* Label */}
          <div className="ml-2 min-w-0">
            <p className={`text-sm font-medium truncate ${rule.is_active ? "text-[#0f172a]" : "text-slate-400"}`}>
              {rule.label}
            </p>
            {rule.description && (
              <p className="text-slate-400 text-xs truncate mt-0.5">{rule.description}</p>
            )}
          </div>
        </div>

        {/* Right: timestamp + actions */}
        <div className="flex items-center gap-3 shrink-0">
          <p className="text-slate-300 text-xs whitespace-nowrap">
            {new Date(rule.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </p>

          {/* Edit */}
          <button
            onClick={() => onEdit(rule)}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
            title="Edit"
          >
            <i className="ri-pencil-line text-sm"></i>
          </button>

          {/* Delete */}
          <button
            onClick={() => onDelete(rule)}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all cursor-pointer"
            title="Delete"
          >
            <i className="ri-delete-bin-line text-sm"></i>
          </button>

          {/* Toggle */}
          <button
            onClick={() => onToggle(rule)}
            disabled={toggling}
            title={rule.is_active ? "Deactivate" : "Activate"}
            className={`relative w-11 h-6 rounded-full transition-all cursor-pointer disabled:opacity-50 ${
              rule.is_active ? "bg-emerald-400" : "bg-white/10"
            }`}
          >
            {toggling ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              </span>
            ) : (
              <span
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${
                  rule.is_active ? "left-[22px]" : "left-0.5"
                }`}
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Empty State ────────────────────────────────────────────── */
function EmptyState({ hasRules, onNew }: { hasRules: boolean; onNew: () => void }) {
  return (
    <div className="flex flex-col items-center gap-5 py-20">
      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-blue-50 text-[#C8A96E]">
        <i className="ri-flashlight-line text-3xl"></i>
      </div>
      <div className="text-center max-w-sm">
        <p className="text-[#0f172a] font-semibold text-base mb-1.5">
          {hasRules ? "No rules match your filters" : "No automation rules yet"}
        </p>
        <p className="text-slate-400 text-sm leading-relaxed">
          {hasRules
            ? "Try adjusting your search or filter to find what you're looking for."
            : "Create your first rule to automate actions when events happen in your studio."}
        </p>
      </div>
      {!hasRules && (
        <button
          onClick={onNew}
          className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line"></i>
          </span>
          Create first rule
        </button>
      )}
    </div>
  );
}
